import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import BidService from '../../../../services/BidService';
import SystemConfigService from '../../../../services/SystemConfigService';
import useMpBidTracking from '../../../../_shared/hooks/useMixpanelBidTracking';
import { formatDistance } from '../../../../_shared/helpers/formatting';
import ReducerConstants from '../../../../_shared/constants/reducers';
import DISTANCE_UNITS from '../../../../_shared/types/distanceUnits';
import timeouts from '../../../../_shared/constants/timeouts';
import Alert from '../../../../_shared/components/Alert';
import getQuickBidIncrements from '../../../../_shared/utils/getQuickBidIncrements';
import BidBadge from '../../../../_shared/components/BidBadge';
import {
  BidConfirmationModalType,
  BidSource,
  BidStatus,
  BidStatusName,
  BidType
} from '../../../../_shared/types/auction';
import FloorMeter from '../../../FloorMeter';
import ListBidBox from './ListBidBox';
import TradeDisclosures from './TradeDisclosures';
import TradeHeader from './TradeHeader';
import TradeRevDirect from '../../../Trd';
import bidErrors from '../../../../_shared/constants/errorCodes';
import mixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import stringToLiteral from '../../../../_shared/helpers/stringToLiteral';
import usePlaceBid from '../../../../_shared/hooks/usePlaceBid';
import usePopulateBidContext from '../../../../_shared/hooks/usePopulateBidContext';
import useManufacturerList from '../../../../_shared/hooks/vehicle/useManufacturerList';
import theme from '../../../../_shared/themes';
import { ChatStatus } from '../../../../_shared/types/chat';
import { ListVariant, TradeState } from '../../../../_shared/types/trade';
import copyToClipBoard from '../../../../_shared/utils/copyToClipBoard';
import ErrorService from '../../../../services/ErrorService';
import MixpanelService from '../../../../services/MixpanelService';
import { useOmpDealerContext } from '../../../Vdp/OmpDealerProvider';
import {
  BodyWrapper,
  DistanceBox,
  FooterWrapper,
  StyledBiddingBadges,
  StyledBiddingSection,
  StyledBody,
  StyledDistanceWrapper,
  StyledTradeName,
  StyledTradeStateSection,
  Wrapper,
  StyledCopyToClipboard
} from './Styles';
import TRToast from '../../../../_shared/helpers/TRToast';

const propTypes = {
  testId: PropTypes.string.isRequired,
  tradeSummary: PropTypes.shape({
    id: PropTypes.number,
    mileage: PropTypes.number,
    odometerType: PropTypes.string,
    transmission: PropTypes.string,
    isAsIs: PropTypes.bool,
    hasDisclosures: PropTypes.bool,
    isTrdSeller: PropTypes.bool,
    isLanded: PropTypes.bool,
    distanceBucket: PropTypes.number,
    isWatchlisted: PropTypes.bool
  }).isRequired
};

const Content = ({
  bidModalRef,
  isAuctionEnded,
  isOmp = false,
  onAuctionEnded,
  onConfirmOpen,
  onPendingActiveBid,
  setTradeSummary,
  testId,
  tradeSummary,
  tradeSummary: {
    auctionSummary,
    dealer,
    details,
    distanceBucket,
    hasDisclosures,
    hasVehicleHistoryReport,
    id: tradeId,
    isAsIs,
    isLanded,
    mileage,
    network,
    odometerType,
    statusSummary,
    tradeFloorPrice,
    tradeStateName,
    transmission,
    trim,
    upcomingProxy,
    vin
  },
  variant
}) => {
  const bidService = BidService.get();
  const { isMultiOrgAndEnabled } = useOmpDealerContext();
  const [bidAmount, setBidAmount] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [minBidAmount, setMinBidAmount] = useState(50);
  const [bidIncrements, setBidIncrements] = useState(null);
  const [bidMpEventName, setBidMpEventName] = useState('');
  const [bidType, setBidType] = useState('');

  const { setBidContext, setBidModalOpen, bidContext } =
    bidModalRef?.current ?? {};
  const { confirmation, highBid, outbidDealerGroup, outbidDealership } =
    bidContext ?? {};
  const [
    tradeState,
    user,
    configurations,
    bidList,
    liveBid,
    sseConnectionStatus
  ] = useSelector(
    state => [
      state?.ui?.nav?.secondary?.context,
      state[ReducerConstants.AUTH]?.user,
      state[ReducerConstants.AUTH]?.system?.configurations,
      state[ReducerConstants.BID_LIST],
      state[ReducerConstants.LIVE_BIDS][tradeId],
      state[ReducerConstants.SSE_CONNECTION_STATUS]
    ],
    shallowEqual
  );
  const { data: manufacturers } = useManufacturerList();
  const { getDistanceConfigurations } = SystemConfigService.get(configurations);
  const distanceConfigurations = getDistanceConfigurations();

  const bidStatus = BidStatus[auctionSummary?.userBidStatus];
  const [trackShowConfirmModal, setTrackShowConfirmModal] = useState(false);
  const [trackHasBid, setTrackHasBid] = useState(false);
  const [trackBidFlag, setTrackBidFlag] = useState(false);
  const [trackBidErrorFlag, setTrackBidErrorFlag] = useState(false);
  const [mpBidError, setMpBidError] = useState([]);
  const [bidSuccess, setBidSuccess] = useState(false);
  const showQnAIcon = statusSummary?.chatStatus === ChatStatus.Activity;

  const displayDistanceRange = () => {
    let distance;
    let distanceText;
    if (distanceBucket < 0 || !distanceConfigurations) {
      return null;
    }

    if (distanceBucket >= distanceConfigurations.length) {
      distance = formatDistance(
        distanceConfigurations[distanceConfigurations.length - 1]
      );
      if (!distance) {
        return null;
      }

      distanceText = (
        <FormattedMessage
          id="list.card.distance.over"
          values={{
            distance
          }}
        />
      );
    } else {
      distance = formatDistance(distanceConfigurations[distanceBucket]);
      if (!distance) {
        return null;
      }

      distanceText = (
        <FormattedMessage
          id="list.card.distance.within"
          values={{
            distance
          }}
        />
      );
    }

    return (
      <DistanceBox testId={`${testId}-footer`}>
        {distanceText} {DISTANCE_UNITS.KM}
      </DistanceBox>
    );
  };

  const toaster = component => {
    if (component?.props) {
      TRToast(component, {
        autoClose:
          component.props.variant !== 'success' ? timeouts.ERROR : timeouts.INFO
      });
    }
  };

  const getProxyAmount = () => {
    if (tradeState === TradeState.active) {
      return auctionSummary?.proxyAmount > auctionSummary?.maxBid
        ? auctionSummary?.proxyAmount
        : undefined;
    }
    return upcomingProxy?.proxyAmount;
  };

  // Updates the BidContext with this component's tradeSummary data
  const populateBidContext = usePopulateBidContext({
    auctionSummary,
    bidSource: BidSource.LIST_VIEW,
    isAuctionEnded,
    isMultiOrgAndEnabled,
    setBidContext,
    tradeState,
    tradeSummary,
    upcomingProxy,
    user
  });

  /**
   * Validates the form
   */
  const validateForm = listBidAmount => {
    const {
      validationErrors: { bidValidationErrors }
    } = bidService.validateForm({
      bidAmount: listBidAmount,
      currentBidAmount:
        tradeState === TradeState.active ? auctionSummary?.maxBid : undefined,
      isSettingProxy: false,
      bidStatus,
      minBidAmount,
      auctionSummary,
      configurations,
      tradeState,
      mpBidError
    });
    const formValid = !(bidValidationErrors.length > 0);
    setFormErrors({
      ERROR_BID: bidValidationErrors.length > 0
    });

    // TODO: Move this logic into the bid service, and restructure the return out of it
    if (!formValid) {
      toaster(<Alert variant="error" messages={bidValidationErrors} />);
    }

    return formValid;
  };
  const auctionId = auctionSummary?.id;
  const bidCreate = usePlaceBid({
    auctionId,
    isOmp,
    tradeId
  });

  /**
   * Places the bid
   */
  const submitBidClick = async listBidAmount => {
    setFormSubmitting(true);

    const onSuccessfulBid = data => {
      setFormSubmitting(false);
      setBidSuccess(true);
      setTrackBidFlag(true);

      if (!sseConnectionStatus.isConnected) {
        // Update Bid Context from /bid/create response
        setTradeSummary(currentTradeSummary => ({
          ...currentTradeSummary,
          activeExpiryTime: data?.auctionSummary?.activeExpiryTime,
          auctionSummary: {
            ...currentTradeSummary.auctionSummary,
            ...data?.auctionSummary
          },
          bidStatus: data?.auctionSummary?.userBidStatus,
          currentBidAmount: data?.auctionSummary?.maxBid,
          currentProxyAmount: data?.auctionSummary?.proxyAmount,
          numberOfBids: data?.auctionSummary?.numBids,
          proxyAmount: data?.auctionSummary?.proxyAmount
        }));
      }

      setBidAmount(listBidAmount); // TODO: Can potentially update 'auctionSummary?.maxBid' context here, and avoid maintaining its own state var. Investigate this.
      if (onPendingActiveBid && sseConnectionStatus?.isConnected) {
        onPendingActiveBid({
          tradeId,
          bidAmount: Number.parseInt(data?.auctionSummary?.maxBid, 10)
        });
      }

      toaster(
        <Alert
          variant="success"
          messages={[<FormattedMessage id="vdp.success.bidPlaced" />]}
        />
      );
    };

    const onErrorBidding = error => {
      const { errorCode, message } = error;
      const errorMessage = stringToLiteral(message);

      setFormSubmitting(false);
      if (errorMessage) {
        ErrorService.trackError(error);
        if (errorCode === bidErrors.AUCTION_ENDED) {
          onAuctionEnded(true);
        }
        toaster(<Alert variant="error" messages={[errorMessage]} />);
        mpBidError.push(errorMessage);
        setBidSuccess(false);
        setTrackBidErrorFlag(true);
        setBidSuccess(false);
      } else {
        toaster(
          <Alert
            variant="error"
            messages={[<FormattedMessage id="vdp.error.placingBid" />]}
          />
        );
      }
    };

    bidCreate.mutate(
      {
        bidAmount: listBidAmount
      },
      { onSuccess: onSuccessfulBid, onError: onErrorBidding }
    );

    setBidContext(context => ({
      ...context,
      confirmation: false,
      highBid: false,
      outbidDealerGroup: false,
      outbidDealership: false
    }));
  };

  const userDealerGroupIds =
    isMultiOrgAndEnabled && user?.multiOrgDealerGroupIds?.length
      ? user?.multiOrgDealerGroupIds
      : user?.dealerGroupIds;
  const isOutbidDealer =
    auctionSummary?.topBidderSummary?.companyId &&
    auctionSummary?.topBidderSummary?.companyId === user?.companyId &&
    user?.id !== auctionSummary?.topBidderSummary?.id;
  const isSameDGN =
    userDealerGroupIds?.length &&
    auctionSummary?.topBidderSummary?.dealerGroupIds?.some(
      id => userDealerGroupIds?.indexOf(id) !== -1
    );
  const isOutbidDGN =
    auctionSummary?.topBidderSummary?.dealerGroupIds?.length &&
    (isSameDGN || auctionSummary?.topBidderSummary?.isSameDealerGroup) &&
    auctionSummary?.topBidderSummary?.id !== user?.id;

  const onBidButtonClick = (listBidAmount, isQuickBid) => {
    // Initial check for warning modals on open
    const confirmationTypes = [];

    const isFirstBid =
      !bidStatus || bidStatus === BidStatusName[BidStatus.none];

    const isBidHigh =
      listBidAmount >= auctionSummary?.highBidThreshold &&
      (!auctionSummary?.maxBid ||
        auctionSummary?.maxBid < auctionSummary?.highBidThreshold);

    setBidMpEventName(
      isQuickBid ? mixpanelConfig.names.quickBid : mixpanelConfig.names.placeBid
    );
    setBidType(isQuickBid ? BidType.quickBid : BidType.manual);

    if (listBidAmount) {
      const onConfirmCallback = async () => {
        const formValid = validateForm(listBidAmount);
        if (!formValid) {
          return;
        }
        setTrackHasBid(true);

        await submitBidClick(listBidAmount);
      };
      const onCancelCallback = () => {
        setTrackHasBid(false);
      };

      // If user's first bid on trade card
      if (isFirstBid) confirmationTypes.push(BidConfirmationModalType.legal);

      // If the bid is very high
      if (isBidHigh)
        confirmationTypes.push(BidConfirmationModalType.overBidThreshold);

      if (isOutbidDealer) {
        // If there is already another bid from the same dealership
        confirmationTypes.push(BidConfirmationModalType.outbidDealership);
        setBidContext(context => ({ ...context, isOutBidSameDealer: true }));
      } else if (isOutbidDGN) {
        // Otherwise, if there is already another bid from the same DGN
        confirmationTypes.push(BidConfirmationModalType.outbidDGN);
        setBidContext(context => ({ ...context, isOutBidSameDGN: true }));
      }

      onConfirmOpen(
        confirmationTypes,
        setTrackShowConfirmModal,
        onConfirmCallback,
        onCancelCallback,
        listBidAmount
      );
    } else {
      populateBidContext();
      setBidModalOpen(true);
    }
  };

  const onQuickBidButtonClick = bidIncrement => {
    const newAmount = auctionSummary?.maxBid
      ? auctionSummary?.maxBid + bidIncrement
      : auctionSummary?.minBidThreshold;

    onBidButtonClick(newAmount, true);
  };

  const onProxyButtonClick = () => {
    populateBidContext();
    setBidModalOpen(true);
  };

  const mixpanel = MixpanelService.get();

  const copyToClipBoardHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    copyToClipBoard(vin);

    TRToast(
      <Alert
        variant="information"
        messages={[<FormattedMessage id="vdp.success.vinCopied" />]}
      />
    );

    mixpanel.track(mixpanelConfig.names.copyVinToClipBoard, {
      trade_id: tradeId,
      trade_state: tradeStateName,
      source: variant
    });
  };

  const onBidAmountChange = listBidAmount => {
    const amount = Math.max(listBidAmount || 0, auctionSummary?.maxBid || 0);
    const increment = getQuickBidIncrements(
      auctionSummary?.maxBid || 0,
      configurations
    );

    setBidAmount(amount);
    setBidIncrements(increment);
  };

  useEffect(() => {
    const increment = getQuickBidIncrements(
      auctionSummary?.maxBid || auctionSummary?.minBidThreshold,
      configurations
    );

    const startingAmount = auctionSummary?.maxBid
      ? auctionSummary?.maxBid + increment
      : auctionSummary?.minBidThreshold;

    setBidIncrements(increment);
    setMinBidAmount(startingAmount);
  }, [
    configurations,
    auctionSummary?.maxBid,
    auctionSummary?.minBidThreshold,
    bidStatus
  ]);

  useEffect(() => {
    if (!setBidContext) {
      return;
    }

    setBidContext(context => ({
      ...context,
      bidList
    }));
  }, [bidList, setBidContext]);

  useMpBidTracking({
    tradeDetails: { auctionSummary, details, id: tradeId },
    trackBidFlag,
    trackProxyFlag: false,
    trackBidErrorFlag,
    trackHasBid,
    trackShowConfirmModal,
    mpBidError,
    proxyAmount: null,
    numberOfBids: auctionSummary?.numBids,
    bidStatus,
    bidAmount,
    liveBid,
    tradeState,
    manufacturers,
    bidSource: BidSource.LIST_BIDBOX,
    bidMpEventName,
    bidType,
    setMpBidError,
    setTrackBidFlag,
    setTrackProxyFlag: null,
    setTrackBidErrorFlag,
    setTrackHasBid,
    setTrackShowConfirmModal,
    confirmationMpTrackers: {
      confirmation,
      highBid,
      outbidDealerGroup,
      outbidDealership
    },
    bidUsers: bidList?.users,
    userId: user?.id
  });

  return (
    <Wrapper>
      <BodyWrapper>
        <StyledBody>
          <TradeHeader
            testId={testId}
            mileage={mileage}
            odometerType={odometerType}
            transmission={transmission}
            trim={trim}
          />
          <TradeDisclosures
            testId={testId}
            isAsIs={isAsIs}
            hasDisclosures={hasDisclosures}
            isLanded={isLanded}
            showQnAIcon={showQnAIcon}
          />
          {vin && variant === ListVariant.SEARCH && (
            <StyledCopyToClipboard
              label={vin}
              color={theme.COLORS.NEUTRAL_30}
              onClick={copyToClipBoardHandler}
              testId="vinCopyButton"
            />
          )}
        </StyledBody>
        {variant !== ListVariant.SEARCH && (
          <StyledBiddingBadges>
            {tradeState === TradeState.active && (
              <>
                <FloorMeter
                  testId={testId}
                  tradeId={tradeId}
                  variant={variant}
                  isFloorPriceMet={tradeFloorPrice?.isFloorPriceMet}
                  hasFloorPrice={tradeFloorPrice?.hasFloorPrice}
                  isCloseToFloorPrice={tradeFloorPrice?.isCloseToFloorPrice}
                />
                {(auctionSummary?.userBidStatus === BidStatus.topBidder ||
                  auctionSummary?.userBidStatus === BidStatus.proxy) && (
                  <BidBadge testId="bid-status-list" variant="success">
                    {/* High bid! */}
                    <FormattedMessage id="vdp.highbid" />
                  </BidBadge>
                )}

                {auctionSummary?.userBidStatus === BidStatus.outbid && (
                  <BidBadge testId="bid-status-list" variant="alert">
                    {/* Outbid! */}
                    <FormattedMessage id="vdp.outbid" />
                  </BidBadge>
                )}
              </>
            )}
          </StyledBiddingBadges>
        )}
        {variant === ListVariant.SEARCH && tradeStateName && (
          <StyledTradeStateSection isOmp={isOmp}>
            <FormattedMessage id="list.currently.in" />
            <StyledTradeName isOmp={isOmp}>
              <FormattedMessage
                id={
                  isOmp
                    ? `tradeState.${tradeStateName.toLowerCase()}.omp`
                    : `tradeState.${tradeStateName.toLowerCase()}`
                }
              />
            </StyledTradeName>
          </StyledTradeStateSection>
        )}
      </BodyWrapper>
      <FooterWrapper>
        <StyledDistanceWrapper>
          <TradeRevDirect
            testId={testId}
            displayType="list"
            dealer={dealer}
            network={network}
            isAtAdesa={tradeSummary?.isAtAdesaLocation}
          />
          {displayDistanceRange()}
        </StyledDistanceWrapper>
        <StyledBiddingSection>
          {variant === ListVariant.LIST &&
            ((tradeState === TradeState.active && !isAuctionEnded) ||
              (tradeState === TradeState.upcoming &&
                variant &&
                upcomingProxy?.bidAmount &&
                upcomingProxy?.proxyAmount)) && (
              <ListBidBox
                testId={testId}
                bidAmount={bidAmount}
                bidIncrements={bidIncrements}
                bidStatus={bidStatus}
                bidMpEventName={bidMpEventName}
                bidType={bidType}
                currentBidAmount={
                  tradeState === TradeState.upcoming
                    ? upcomingProxy?.bidAmount
                    : auctionSummary?.maxBid
                }
                currentProxyAmount={getProxyAmount()}
                minBidAmount={minBidAmount}
                isAuctionEnded={isAuctionEnded}
                isLoading={false}
                numberOfBids={auctionSummary?.numBids}
                hasVehicleHistoryReport={hasVehicleHistoryReport}
                onBidButtonClick={onBidButtonClick}
                onQuickBidButtonClick={onQuickBidButtonClick}
                onProxyButtonClick={onProxyButtonClick}
                onBidAmountChange={onBidAmountChange}
                tradeState={tradeState}
                tradeId={tradeId}
                formErrors={formErrors}
                formSubmitting={formSubmitting}
                tradeSummary={tradeSummary}
                bidSuccess={bidSuccess}
                setBidSuccess={setBidSuccess}
                manufacturers={manufacturers}
              />
            )}
        </StyledBiddingSection>
      </FooterWrapper>
    </Wrapper>
  );
};

Content.propTypes = propTypes;

export default Content;
